// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { QueryRenderer, ReadyState } from "react-relay";
import { connect } from "react-redux";

// chrome-feed imports
import {
  IRendererContainer,
  IRendererProps,
} from "./ChromeAutopilotFeedStatusDefragCardRenderer.d";
import { query } from "./ChromeAutopilotFeedStatusDefragCardRenderer.gql";
// components
import { TailoredDefragSummaryCard } from "@components/tailored-defrag-summary-card";
import { AutopilotSuggestionsCard } from "@components/autopilot-suggestions-card";
// util
import { ChromeAppWrapperView } from "@src/util/chrome-extension.util";
import { PostMessageManager } from "@util/post-message.util";
import { getRenderTimeZone } from "@util/time-zone.util";
import { handleReadyStateError } from "@util/relay.util";
// state
import { IReduxState } from "@reducers/root.reducer";
import {
  lastDefragCardTimestamp,
  lastAutopilotSuggestionsCardTimestamp,
} from "@state/local-storage";
import {
  setAutopilotFeedStatusDefragCardNotificationCount,
  setActiveView,
} from "@actions/chrome-extension.actions";

///////////////
// Component //
///////////////

class ChromeAutopilotFeedStatusDefragCardRendererComponent extends React.PureComponent<
  IRendererProps,
  {}
> {
  private retry?: () => void;
  private refetchInterval?: ReturnType<typeof setInterval>;

  public componentDidMount() {
    this.refetchInterval = setInterval(() => {
      if (this.retry) {
        this.retry();
      }
    }, 30 * 60 * 1000);
  }

  public componentWillUnmount() {
    if (this.refetchInterval) {
      clearInterval(this.refetchInterval);
      delete this.refetchInterval;
    }
  }

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const { environment, orgRelayId, orgTimeZone, fastCrawlFinished } = this.props;

    if (!orgTimeZone || !fastCrawlFinished) {
      return null;
    }

    return (
      <QueryRenderer
        environment={environment}
        query={query}
        variables={{ orgRelayId, timeZone: getRenderTimeZone() }}
        cacheConfig={{ force: true }}
        render={(readyState: ReadyState) => {
          // check for error or show a loader for first load
          if (readyState.error) {
            handleReadyStateError(readyState.error, "ChromeAutopilotFeedStatusDefragCardRenderer");
            return null;
          }

          const org = readyState && readyState.props && (readyState.props.org as ISchema.IOrg);
          const user =
            readyState &&
            readyState.props &&
            readyState.props.viewer &&
            (readyState.props.viewer.user as ISchema.IUser);

          if (!org || !user) {
            return null;
          }

          if (readyState.retry) {
            this.retry = readyState.retry;
          }

          const { tailoredDefragCardSummary } = org;
          const showDefragCard =
            tailoredDefragCardSummary &&
            tailoredDefragCardSummary.__typename !== "GraphEntityError" &&
            tailoredDefragCardSummary.yourEventMoves &&
            tailoredDefragCardSummary.yourEventMoves.length > 0 &&
            parseInt(lastDefragCardTimestamp.get() || "0") < tailoredDefragCardSummary.defragTime;

          const showAutopilotSuggestionsCard =
            tailoredDefragCardSummary &&
            tailoredDefragCardSummary.__typename !== "GraphEntityError" &&
            tailoredDefragCardSummary.knobs.inviteOrganizerKnobs.length +
              tailoredDefragCardSummary.knobs.autopilotMeetingKnobs.length +
              tailoredDefragCardSummary.knobs.lessenAutopilotConstraintKnobs.length >
              0 &&
            parseInt(lastAutopilotSuggestionsCardTimestamp.get() || "0") <
              tailoredDefragCardSummary.defragTime;

          const shouldNotifyExtensionOfDefragSummaryCard =
            !!showDefragCard &&
            (tailoredDefragCardSummary as ISchema.ITailoredDefragCardSummary).defragTime >
              this.props.lastViewedFeedMillis;

          const shouldNotifyExtensionOfAutopilotSuggestionsCard =
            !!showAutopilotSuggestionsCard &&
            (tailoredDefragCardSummary as ISchema.ITailoredDefragCardSummary).defragTime >
              this.props.lastViewedFeedMillis;

          const extensionNotificationCount =
            shouldNotifyExtensionOfDefragSummaryCard &&
            shouldNotifyExtensionOfAutopilotSuggestionsCard
              ? 2
              : shouldNotifyExtensionOfDefragSummaryCard ||
                shouldNotifyExtensionOfAutopilotSuggestionsCard
              ? 1
              : 0;

          PostMessageManager.sendAutopilotCardsNotificationCount(extensionNotificationCount);
          if (extensionNotificationCount > 0) {
            this.props.setAutopilotFeedStatusDefragCardNotificationCount(
              extensionNotificationCount,
            );
            if (!this.props.chromeExtensionIdle) {
              // if not idle, go to feed
              this.props.setActiveView(ChromeAppWrapperView.Feed);
            }
          }

          if (this.props.updateCardCount) {
            this.props.updateCardCount(
              (showDefragCard ? 1 : 0) + (showAutopilotSuggestionsCard ? 1 : 0),
            ); // count callback to know if we need to set empty state
          }

          return (
            <React.Fragment>
              {showDefragCard ? (
                <TailoredDefragSummaryCard removeCard={() => this.forceUpdate()} org={org} />
              ) : null}
              {showAutopilotSuggestionsCard ? (
                <AutopilotSuggestionsCard removeCard={() => this.forceUpdate()} org={org} />
              ) : null}
            </React.Fragment>
          );
        }}
      />
    );
  }
}

///////////
// Redux //
///////////
function mapStateToProps(state: IReduxState, _ownProps: IRendererContainer) {
  return {
    lastViewedFeedMillis: state.chromeFeed.lastViewedFeedMillis,
    chromeExtensionIdle: state.webExtension.webExtensionIdle,
  };
}

export const ChromeAutopilotFeedStatusDefragCardRenderer = connect(mapStateToProps, {
  setAutopilotFeedStatusDefragCardNotificationCount,
  setActiveView,
})(ChromeAutopilotFeedStatusDefragCardRendererComponent);
